import { FC, useMemo } from 'react';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import './TeamDayHiddenCalendars.scss';
import {
  buildHiddenCalendarColumnStyles,
  formatTeamMemberOverflowList,
  getFirstName,
  getHiddenCalendarCardStyle,
} from '../../utils';
import type { TeamDayResourceWithColumn } from '../types';

interface TeamDayHiddenCalendarsProps {
  hiddenResources: TeamDayResourceWithColumn[];
  resourceCount: number;
}

export const TeamDayHiddenCalendarStyles: FC<{
  hiddenResources: TeamDayResourceWithColumn[];
}> = ({ hiddenResources }) => {
  const columnStyles = useMemo(
    () => buildHiddenCalendarColumnStyles(hiddenResources),
    [hiddenResources]
  );

  if (!columnStyles) {
    return null;
  }

  return <style>{columnStyles}</style>;
};

export const TeamDayHiddenCalendarCards: FC<TeamDayHiddenCalendarsProps> = ({
  hiddenResources,
  resourceCount,
}) => {
  const { t } = useTranslation();

  if (!hiddenResources.length) {
    return null;
  }

  return (
    <div className="team-day-hidden-calendars__cards" aria-hidden="true">
      {hiddenResources.map((resource) => (
        <div
          key={resource.id}
          className="team-day-hidden-calendars__card"
          style={getHiddenCalendarCardStyle(resource, resourceCount)}
        >
          <span className="team-day-hidden-calendars__card-icon">
            <VisibilityOffIcon />
          </span>
          <Typography
            variant="body2"
            component="span"
            className="team-day-hidden-calendars__card-title"
          >
            {t('team.day.calendarHidden')}
          </Typography>
          <Typography
            variant="subtitle2"
            component="span"
            className="team-day-hidden-calendars__card-copy"
          >
            {t('team.day.hiddenCalendarCardCopy', { name: getFirstName(resource.name, t) })}
          </Typography>
        </div>
      ))}
    </div>
  );
};

export const TeamDayHiddenCalendarSummary: FC<{
  hiddenResources: TeamDayResourceWithColumn[];
}> = ({ hiddenResources }) => {
  const { t } = useTranslation();
  const copy = useMemo(
    () =>
      formatTeamMemberOverflowList(
        hiddenResources.map((r) => getFirstName(r.name, t)),
        3,
        t
      ),
    [hiddenResources, t]
  );

  if (!hiddenResources.length) {
    return null;
  }

  return (
    <div className="team-day-hidden-calendars__summary">
      <span className="team-day-hidden-calendars__summary-icon" aria-hidden="true">
        <VisibilityOffIcon />
      </span>
      <Typography
        variant="subtitle2"
        component="span"
        className="team-day-hidden-calendars__summary-copy"
      >
        {t('team.day.hiddenCalendarSummary', { names: copy, count: hiddenResources.length })}
      </Typography>
    </div>
  );
};
